import { Column, Row, Tooltip, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Divider } from '~/commonComponents';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { ChatOutlineIcon, ChevronRight, ShareIcon } from '~/images/icons';
import UserIcon from '~/images/user-icon.svg';
import type { IOnsiteInterviewCardProps } from './interfaces';
import { ClipLoader } from 'react-spinners';
import { pluralize } from '~/utils/String';

export const OnsiteInterviewCard = ({
  candidatesAppliedCount,
  date,
  endsAt,
  isInvited = false,
  isInviting = false,
  onsiteInterviewId,
  profession,
  rate,
  specialty,
  startsAt,
  subProfession = '',
  variant,
  onConfirm,
  onDecline,
  onInvite,
  onMessage,
  onViewDetails,
}: IOnsiteInterviewCardProps) => {
  const hasMultipleCandidates = candidatesAppliedCount ? candidatesAppliedCount > 1 : false;
  const isAppliedVariant = variant === 'applied';
  const isAvailableVariant = variant === 'available';
  const isCompletedVariant = variant === 'completed';
  const isScheduledVariant = variant === 'scheduled';

  return (
    <Column
      className="cursor-pointer shadow-xs"
      data-cy={`onsite-interview-card-${onsiteInterviewId}`}
      py={4}
      px={6}
      style={{ backgroundColor: colors.shades[0], borderRadius: 8 }}
      onClick={() => onViewDetails?.(onsiteInterviewId)}
    >
      <Row align="center" justify="between">
        <Typography as="span" bold size="body_large">
          {date}
        </Typography>
        <Row role="button" style={{ cursor: 'pointer' }}>
          <Typography as="span" bold color={colors.secondary[500]} size="body_large">
            View Details
          </Typography>
          <ChevronRight color={colors.secondary[500]} size={24} />
        </Row>
      </Row>
      <Divider color={colors.neutral[100]} height={1} marginVertical={8} />
      <Row align="end" gap={1}>
        <Column className="flex-1" style={{ marginTop: 4 }}>
          <Typography as="span">
            {UserSubProfession.getName(subProfession) || UserProfession.getName(profession)} /{' '}
            {specialty}
          </Typography>
          <Typography as="span">
            {startsAt} – {endsAt} &bull; ${rate?.toFixed(2)}/hr
          </Typography>
        </Column>
        {(isAppliedVariant || isAvailableVariant) && hasMultipleCandidates && (
          <Tooltip
            content={`${candidatesAppliedCount} ${pluralize('Candidate', candidatesAppliedCount || 0)} have applied`}
            placement="left"
          >
            <Row align="center" justify="center" gap={1}>
              <img alt="user-icon" src={UserIcon} height={24} width={24} />
              <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
                {candidatesAppliedCount}
              </Typography>
            </Row>
          </Tooltip>
        )}
      </Row>
      {!isCompletedVariant && (
        <Divider color={colors.neutral[100]} height={1} marginVertical={8} />
      )}
      <Row align="center" gap={4} justify="center" style={{ marginTop: 6 }}>
        {isAppliedVariant && (
          <>
            <button
              className="shadow-xs hover__shadow-sm"
              data-cy="candidate-action-decline"
              style={{
                alignItems: 'center',
                background: colors.secondary[500],
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDecline?.(onsiteInterviewId);
              }}
            >
              <Typography as="span" bold color={colors.shades[0]} size="body_large">
                Decline
              </Typography>
            </button>
            <button
              className="shadow-xs hover__shadow-sm"
              data-cy="candidate-action-confirm"
              style={{
                alignItems: 'center',
                background: colors.primary[500],
                borderRadius: 48,
                display: 'flex',
                gap: 6,
                justifyContent: 'center',
                paddingBlock: 14,
                paddingInline: 16,
                width: 140,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onConfirm?.(onsiteInterviewId);
              }}
            >
              <Typography as="span" bold color={colors.shades[0]} size="body_large">
                Confirm
              </Typography>
            </button>
          </>
        )}
        {isAvailableVariant && (
          <button
            className="shadow-xs hover__shadow-sm"
            data-cy="candidate-action-invite"
            disabled={isInvited}
            style={{
              alignItems: 'center',
              background: isInvited ? colors.primary[200] : colors.primary[500],
              borderRadius: 48,
              cursor: isInvited ? 'default' : undefined,
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              paddingBlock: 14,
              paddingInline: 16,
              width: 140,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onInvite?.(onsiteInterviewId);
            }}
          >
            {isInviting ? (
              <ClipLoader color={colors.shades[0]} size={16} />
            ) : (
              <>
                <ShareIcon color={colors.shades[0]} />
                <Typography as="span" bold color={colors.shades[0]} size="body_large">
                  {isInvited ? 'Invited' : 'Invite'}
                </Typography>
              </>
            )}
          </button>
        )}
        {isScheduledVariant && (
          <button
            className="shadow-xs hover__shadow-sm"
            data-cy="candidate-action-message"
            style={{
              alignItems: 'center',
              background: colors.primary[500],
              borderRadius: 48,
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              paddingBlock: 14,
              paddingInline: 16,
              width: 140,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMessage?.(onsiteInterviewId);
            }}
          >
            <ChatOutlineIcon color={colors.shades[0]} />
            <Typography as="span" bold color={colors.shades[0]} size="body_large">
              Message
            </Typography>
          </button>
        )}
      </Row>
    </Column>
  );
};
